import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiGeneralParams, IApiPaginatedResponse, IApiResponse } from "@/types";
import type {
  EventParticipantsData,
  EventReportResponse,
  IEventDetails,
  IEventStats,
  IGenerateEventMeetingLinkPayload,
  IGenerateEventMeetingLinkResponse,
  IInviteeGroupsResponse,
  ISingleCompanyEvent,
} from "@/types/(company)/events.interface";
import { createApi } from "@reduxjs/toolkit/query/react";

export const companyEventsApi = createApi({
  reducerPath: "companyEventsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["GetAllEvents", "GetEventStats", "GetEventById", "GetEventParticipants"],
  endpoints: (builder) => ({
    companyGetAllEvents: builder.query<
      IApiPaginatedResponse<ISingleCompanyEvent[]>,
      IApiGeneralParams & { pageSize?: number; type?: string; startDateTime?: string; endDateTime?: string; branchId?: number }
    >({
      query: (params) => ({
        url: APIEndpoints.Company.Events.GetAllEvents,
        params,
      }),
      providesTags: ["GetAllEvents"],
    }),
    companyGetEventStats: builder.query<IApiPaginatedResponse<IEventStats>, void>({
      query: () => ({
        url: APIEndpoints.Company.Events.GetEventStats,
      }),
      providesTags: ["GetEventStats"],
    }),
    companyCreateEvent: builder.mutation<IApiResponse<void>, FormData>({
      query: (payload) => ({
        url: APIEndpoints.Company.Events.CreateEvent,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["GetAllEvents", "GetEventStats"],
    }),
    companyUpdateEvent: builder.mutation<IApiResponse<void>, { eventId: string; payload: FormData }>({
      query: ({ eventId, payload }) => ({
        url: APIEndpoints.Company.Events.UpdateEvent(eventId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["GetAllEvents", "GetEventStats", "GetEventById"],
    }),
    companyGenerateEventMeetingLink: builder.mutation<
      IApiResponse<IGenerateEventMeetingLinkResponse>,
      IGenerateEventMeetingLinkPayload
    >({
      query: (payload) => ({
        url: APIEndpoints.Company.Events.GenerateEventMeetingLink,
        method: "POST",
        body: payload,
      }),
    }),
    companyGetEventById: builder.query<IApiPaginatedResponse<IEventDetails>, { eventId: string }>({
      query: ({ eventId }) => ({
        url: APIEndpoints.Company.Events.GetEventById(eventId),
      }),
      providesTags: ["GetEventById"],
    }),
    companyGetEventParticipants: builder.query<
      IApiPaginatedResponse<EventParticipantsData>,
      IApiGeneralParams & { eventId: string }
    >({
      query: (params) => ({
        url: APIEndpoints.Company.Events.GetEventParticipants(params.eventId),
        params,
      }),
      providesTags: ["GetEventParticipants"],
    }),
    companyCancelEvent: builder.mutation<IApiResponse<void>, { eventId: string }>({
      query: ({ eventId }) => ({
        url: APIEndpoints.Company.Events.CancelEvent(eventId),
        method: "PUT",
      }),
      invalidatesTags: ["GetEventById", "GetAllEvents", "GetEventStats"],
    }),
    companyGetEventReport: builder.query<IApiResponse<EventReportResponse>, { eventId: string }>({
      query: ({ eventId }) => ({
        url: APIEndpoints.Company.Events.GetEventReport(eventId),
      }),
    }),
    companyGetInviteeGroups: builder.query<IApiResponse<IInviteeGroupsResponse>, void>({
      query: () => ({
        url: APIEndpoints.Company.Events.GetInviteeGroups,
      }),
    }),
    deleteEvent: builder.mutation<IApiResponse<void>, { eventId: string | number }>({
      query: ({ eventId }) => ({
        url: APIEndpoints.Company.Events.GetEventById(eventId),
        method: "DELETE",
      }),
      invalidatesTags: ["GetAllEvents", "GetEventStats"],
    }),
  }),
});

export const {
  useCompanyGetAllEventsQuery,
  useCompanyGetEventByIdQuery,
  useCompanyGetEventStatsQuery,
  useCompanyCreateEventMutation,
  useCompanyCancelEventMutation,
  useCompanyGetEventParticipantsQuery,
  useCompanyUpdateEventMutation,
  useCompanyGenerateEventMeetingLinkMutation,
  useLazyCompanyGetEventReportQuery,
  useDeleteEventMutation,
  useCompanyGetInviteeGroupsQuery,
} = companyEventsApi;
